import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSyncAlt, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { Card, Col, Row, Button, Spinner } from 'react-bootstrap';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { TooltipComponent, GridComponent, LegendComponent, GraphicComponent } from 'echarts/components';
import { BarChart, LineChart, RadarChart, HeatmapChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';

import { usePostAiAssuranceMutation, useGetProjectListsQuery } from '../project-details/data-access/project.api';
import type { Project } from '@app/shared/types/project.type';
import { fetchCSAPlusProjects } from '../../csa-plus/data-access/csa-plus.api';
import { fetchMTCPlusProjects } from '../../mtc_plus/data-access/mtc-plus.api';

import {
  computeMetrics,
  computeRiskPosture,
  filterAssuranceRowsForProjects,
  formatAssuranceProjectLabel,
  getHeatmapMatrix,
  getGovernanceTrendByDate,
  type TaggedAssuranceProject
} from './aiAssurance.utils';

import type { AiAssuranceMetrics, RiskPosture } from './aiAssurance.utils';

import {
  getScorecardGaugeOption,
  getRadarOption,
  getHeatmapOption,
  getGovernanceTrendOption
} from './aiAssuranceCharts';

echarts.use([
  TooltipComponent,
  GridComponent,
  LegendComponent,
  GraphicComponent,
  BarChart,
  LineChart,
  RadarChart,
  HeatmapChart,
  CanvasRenderer
]);

const ALL_PROJECTS = 'ALL';
const DEFAULT_PERIOD_LABEL = 'Q4 2025';
const TARGET_MATURITY = 80;

const LENS_LABELS = ['Ethics', 'Compliance', 'Governance', 'Safety', 'Security', 'Trust'];

const RISK_LEVEL_COLOR: Record<string, string> = {
  CRITICAL: '#dc3545',
  HIGH: '#fd7e14',
  MEDIUM: '#ffc107',
  LOW: '#28a745',
  MINIMAL: '#20c997'
};

const BAND_COLOR: Record<string, string> = {
  EARLY: '#dc3545',
  EMERGING: '#ff9800',
  DEFINED: '#ffc107',
  ADVANCED: '#8bc34a',
  LEADING: '#4caf50',
  UNKNOWN: '#6c757d'
};

const LENS_RISK_COLOR = (score: number | null) => {
  if (score == null) return '#6c757d';
  if (score >= 60) return '#dc3545';
  if (score >= 40) return '#ffc107';
  return '#28a745';
};

const LENS_RISK_LABEL = (score: number | null): string => {
  if (score == null) return 'N/A';
  if (score >= 60) return 'HIGH';
  if (score >= 40) return 'MEDIUM';
  if (score >= 20) return 'LOW';
  return 'MINIMAL';
};

const AIAssurancePage = () => {
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();

  const projectIdFromUrl = searchParams.get('projectId') || undefined;
  const userId = localStorage.getItem('userId') || '';
  const environment = localStorage.getItem('SELECTED_ENV') || 'DEV';
  const organizationId = localStorage.getItem('organizationId') || '';

  const { data: tscProjectList = [] } = useGetProjectListsQuery(
    { userId, environment, organizationId },
    { skip: !userId || !organizationId }
  );

  const [csaProjectList, setCsaProjectList] = useState<Project[]>([]);
  const [mtcProjectList, setMtcProjectList] = useState<Project[]>([]);

  useEffect(() => {
    if (!userId) {
      setCsaProjectList([]);
      setMtcProjectList([]);
      return;
    }

    let cancelled = false;

    Promise.all([
      fetchCSAPlusProjects({ userId, excludeUploads: true }),
      fetchMTCPlusProjects({ userId, excludeUploads: true })
    ])
      .then(([csaProjects, mtcProjects]) => {
        if (!cancelled) {
          setCsaProjectList(csaProjects);
          setMtcProjectList(mtcProjects);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCsaProjectList([]);
          setMtcProjectList([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [userId]);

  const taggedProjectList = useMemo((): TaggedAssuranceProject[] => {
    const tsc = tscProjectList.map((project: Project) => ({ project, product: 'TSC+' as const }));
    const csa = csaProjectList.map(project => ({ project, product: 'CSA+' as const }));
    const mtc = mtcProjectList.map(project => ({ project, product: 'MTC+' as const }));
    return [...tsc, ...csa, ...mtc];
  }, [tscProjectList, csaProjectList, mtcProjectList]);

  const [projectId, setProjectId] = useState<string>(projectIdFromUrl || ALL_PROJECTS);
  const [periodLabel, setPeriodLabel] = useState(DEFAULT_PERIOD_LABEL);
  const [latestOnly, setLatestOnly] = useState(false);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [metrics, setMetrics] = useState<AiAssuranceMetrics | null>(null);
  const [riskPosture, setRiskPosture] = useState<RiskPosture | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [postAiAssurance, { data: apiData, isLoading, isError, error: apiError }] = usePostAiAssuranceMutation();

  const allowedProjectIds = useMemo((): Set<string> => {
    const ids = new Set<string>(taggedProjectList.map(({ project }) => project.projectId));
    if (projectId !== ALL_PROJECTS && projectId) ids.add(projectId);
    return ids;
  }, [taggedProjectList, projectId]);

  useEffect(() => {
    const isKnownProject = (id: string) => taggedProjectList.some(({ project }) => project.projectId === id);

    if (projectIdFromUrl) {
      setProjectId(
        taggedProjectList.length === 0 || isKnownProject(projectIdFromUrl) ? projectIdFromUrl : ALL_PROJECTS
      );
      return;
    }

    const stored = localStorage.getItem('projectId');

    if (stored === ALL_PROJECTS) {
      setProjectId(ALL_PROJECTS);
      return;
    }

    if (stored && isKnownProject(stored)) {
      setProjectId(stored);
      return;
    }

    setProjectId(ALL_PROJECTS);
  }, [projectIdFromUrl, taggedProjectList]);

  const selectedTaggedProject =
    projectId !== ALL_PROJECTS ? taggedProjectList.find(({ project }) => project.projectId === projectId) : null;

  const projectLabel =
    projectId === ALL_PROJECTS
      ? 'All Projects'
      : selectedTaggedProject
      ? formatAssuranceProjectLabel(selectedTaggedProject)
      : projectId || 'Select a project';

  const loadData = useCallback(() => {
    setError(null);

    postAiAssurance({
      projectId: projectId === ALL_PROJECTS ? ALL_PROJECTS : projectId,
      periodLabel,
      latestOnly: projectId !== ALL_PROJECTS && latestOnly
    });
  }, [postAiAssurance, projectId, periodLabel, latestOnly]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!apiData?.fact_ai_assurance_360?.length) {
      setMetrics(null);
      setRiskPosture(null);
      return;
    }

    const rows = filterAssuranceRowsForProjects(apiData.fact_ai_assurance_360, allowedProjectIds);

    const filteredRows = rows.filter((row: any) => {
      if (!periodLabel) return true;
      return row.period_label === periodLabel;
    });

    const finalRows = filteredRows.length ? filteredRows : rows;

    const m = computeMetrics(finalRows, TARGET_MATURITY);
    setMetrics(m);
    setRiskPosture(m ? computeRiskPosture(m) : null);
  }, [apiData, periodLabel, allowedProjectIds]);

  useEffect(() => {
    if (isError && apiError) {
      setError('message' in apiError ? String(apiError.message) : 'Failed to load AI Assurance data');
    } else {
      setError(null);
    }
  }, [isError, apiError]);

  const allProjectsPath = pathname.startsWith('/csa+')
    ? '/csa+/all-projects'
    : pathname.startsWith('/mtc+')
    ? '/mtc+/all-projects'
    : `/project/${userId}/all-projects`;

  const periods = useMemo(() => {
    const rows = filterAssuranceRowsForProjects(apiData?.fact_ai_assurance_360 || [], allowedProjectIds);
    const unique = Array.from(new Set(rows.map((r: any) => r.period_label).filter(Boolean))) as string[];

    const qOrder: Record<string, number> = { Q1: 1, Q2: 2, Q3: 3, Q4: 4 };

    return unique.sort((a, b) => {
      const [qa, ya] = a.split(' ');
      const [qb, yb] = b.split(' ');

      if (ya !== yb) return Number(yb) - Number(ya);
      return (qOrder[qb] || 0) - (qOrder[qa] || 0);
    });
  }, [apiData, allowedProjectIds]);

  useEffect(() => {
    if (periods.length > 0 && !periods.includes(periodLabel)) {
      setPeriodLabel(periods[0]);
    }
  }, [periods, periodLabel]);

  const plot1Option = useMemo(() => (metrics ? getScorecardGaugeOption(metrics) : null), [metrics]);
  const plot2Option = useMemo(() => (metrics ? getRadarOption(metrics, TARGET_MATURITY) : null), [metrics]);

  const { dims: heatmapDims, mat: heatmapMat } = useMemo(
    () => (metrics ? getHeatmapMatrix(metrics.rows) : { dims: [] as [string, string][], mat: [] as number[][] }),
    [metrics]
  );

  const plot3Option = useMemo(() => {
    if (heatmapDims.length === 0) return null;
    return getHeatmapOption(
      heatmapDims.map((d: [string, string]) => d[0]),
      heatmapMat
    );
  }, [heatmapDims, heatmapMat]);

  const trendData = useMemo(() => (metrics ? getGovernanceTrendByDate(metrics.rows) : null), [metrics]);

  const plot4Option = useMemo(() => {
    if (!trendData || trendData.dates.length === 0) return null;

    return getGovernanceTrendOption(
      trendData.dates,
      trendData.approved,
      trendData.conditional,
      trendData.rejected,
      trendData.not_approved
    );
  }, [trendData]);

  return (
    <div className="ai-assurance-page" style={{ background: '#f8f9fa', minHeight: '100vh', padding: '0' }}>
      <div
        style={{
          background: '#fff',
          borderBottom: '1px solid #e9ecef',
          padding: '12px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12
        }}
      >
        <Link
          to={allProjectsPath}
          className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-2"
          style={{ textDecoration: 'none' }}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          Back
        </Link>

        <h5 className="fw-bold mb-0" style={{ color: '#212529' }}>
          AI Assurance 360°
        </h5>

        <div className="d-flex align-items-center gap-2 flex-wrap">
          <div style={{ position: 'relative' }}>
            <button
              className="btn btn-sm btn-light border d-flex align-items-center gap-2"
              onClick={() => setShowProjectDropdown(v => !v)}
              style={{ minWidth: 220 }}
            >
              <span className="text-truncate" style={{ maxWidth: 180 }}>
                {projectLabel}
              </span>
              <FontAwesomeIcon icon={faChevronDown} size="xs" />
            </button>

            {showProjectDropdown && (
              <div
                style={{
                  position: 'absolute',
                  top: '110%',
                  right: 0,
                  background: '#fff',
                  border: '1px solid #dee2e6',
                  borderRadius: 8,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                  zIndex: 9999,
                  minWidth: 240,
                  maxHeight: 320,
                  overflowY: 'auto'
                }}
              >
                <button
                  className="btn btn-sm w-100 text-start px-3 py-2"
                  style={{
                    borderRadius: 0,
                    background: projectId === ALL_PROJECTS ? '#f0f4ff' : 'transparent',
                    fontWeight: projectId === ALL_PROJECTS ? 600 : 400
                  }}
                  onClick={() => {
                    setProjectId(ALL_PROJECTS);
                    localStorage.setItem('projectId', ALL_PROJECTS);
                    setShowProjectDropdown(false);
                  }}
                >
                  All Projects
                </button>

                {taggedProjectList.map(({ project, product }) => (
                  <button
                    key={`${product}-${project.projectId}`}
                    className="btn btn-sm w-100 text-start px-3 py-2"
                    style={{
                      borderRadius: 0,
                      background: project.projectId === projectId ? '#f0f4ff' : 'transparent',
                      fontWeight: project.projectId === projectId ? 600 : 400
                    }}
                    onClick={() => {
                      setProjectId(project.projectId);
                      localStorage.setItem('projectId', project.projectId);
                      setShowProjectDropdown(false);
                    }}
                  >
                    {formatAssuranceProjectLabel({ project, product })}
                  </button>
                ))}
              </div>
            )}
          </div>

          <select
            className="form-select form-select-sm"
            value={periodLabel}
            onChange={e => setPeriodLabel(e.target.value)}
            style={{ minWidth: 120 }}
          >
            {periods.length > 0 ? (
              periods.map(q => (
                <option key={q} value={q}>
                  {q}
                </option>
              ))
            ) : (
              <option value={periodLabel}>{periodLabel}</option>
            )}
          </select>

          <div className="form-check form-switch mb-0 d-flex align-items-center gap-2" style={{ paddingLeft: 0 }}>
            <input
              type="checkbox"
              className="form-check-input"
              id="latestOnly"
              role="switch"
              checked={latestOnly}
              onChange={e => setLatestOnly(e.target.checked)}
              disabled={projectId === ALL_PROJECTS}
              style={{ marginLeft: 0, cursor: 'pointer' }}
            />
            <label className="form-check-label small text-muted" htmlFor="latestOnly">
              Latest only
            </label>
          </div>

          <Button
            variant="primary"
            size="sm"
            onClick={loadData}
            disabled={isLoading}
            className="d-flex align-items-center gap-2"
          >
            {isLoading ? <Spinner animation="border" size="sm" /> : <FontAwesomeIcon icon={faSyncAlt} />}
            {isLoading ? 'Loading...' : 'Load'}
          </Button>
        </div>
      </div>

      {error && <div className="alert alert-danger mx-4 mt-3 mb-0">{error}</div>}

      {apiData && !apiData.fact_ai_assurance_360?.length && (
        <div className="alert alert-warning mx-4 mt-3 mb-0">No records returned for the selected project.</div>
      )}

      {metrics && riskPosture && (
        <div style={{ padding: '20px 24px' }}>
          <Row className="g-3 mb-3">
            <Col xs={12} md={3}>
              <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 12 }}>
                <Card.Body className="d-flex flex-column align-items-center justify-content-center text-center py-4">
                  <div
                    style={{
                      fontSize: 52,
                      fontWeight: 700,
                      color: BAND_COLOR[metrics.maturity_level] ?? '#6c757d',
                      lineHeight: 1
                    }}
                  >
                    {metrics.maturity_index}
                  </div>
                  <div className="text-muted small mt-1">Maturity Index / 100</div>
                  <span
                    className="badge mt-2 px-3 py-1"
                    style={{
                      background: BAND_COLOR[metrics.maturity_level] ?? '#6c757d',
                      color: '#fff',
                      borderRadius: 20,
                      fontSize: 12,
                      fontWeight: 600
                    }}
                  >
                    {metrics.maturity_level}
                  </span>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} md={3}>
              <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 12 }}>
                <Card.Body className="d-flex flex-column justify-content-center py-4 px-4">
                  <div className="text-muted small mb-1">Target Gap</div>
                  <div
                    style={{
                      fontSize: 26,
                      fontWeight: 700,
                      color: metrics.maturity_index >= metrics.target ? '#28a745' : '#fd7e14'
                    }}
                  >
                    {metrics.maturity_index >= metrics.target
                      ? `+${metrics.maturity_index - metrics.target} above target`
                      : `-${metrics.target - metrics.maturity_index} below target`}
                  </div>
                  <div className="text-muted small mt-1">Target: {metrics.target}</div>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} md={3}>
              <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 12 }}>
                <Card.Body className="d-flex flex-column justify-content-center py-4 px-4">
                  <div className="text-muted small mb-1">Data Confidence</div>
                  <div
                    style={{
                      fontSize: 26,
                      fontWeight: 700,
                      color:
                        metrics.confidence === 'HIGH'
                          ? '#28a745'
                          : metrics.confidence === 'MEDIUM'
                          ? '#ffc107'
                          : '#dc3545'
                    }}
                  >
                    {metrics.confidence}
                  </div>
                  {metrics.confidence === 'LOW' && (
                    <div className="small text-danger mt-1">✗ Low data coverage — scores may be inaccurate</div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} md={3}>
              <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 12 }}>
                <Card.Body className="d-flex flex-column justify-content-center py-4 px-4">
                  <div className="text-muted small mb-2">Review Summary</div>
                  <div className="d-flex gap-3">
                    <span style={{ color: '#28a745', fontWeight: 600 }}>{metrics.approved} approved</span>
                    <span style={{ color: '#ffc107', fontWeight: 600 }}>{metrics.conditional} conditional</span>
                    <span style={{ color: '#dc3545', fontWeight: 600 }}>{metrics.rejected} rejected</span>
                  </div>
                  <div className="text-muted small mt-2">
                    {metrics.total_reviews} reviews across {metrics.total_projects} projects
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: 12 }}>
            <Card.Body className="p-4">
              <h6 className="fw-bold text-muted text-uppercase mb-3" style={{ fontSize: 12, letterSpacing: 1 }}>
                Risk Posture
              </h6>

              <Row className="g-3">
                <Col xs={12} md={4}>
                  <div
                    className="d-flex flex-column align-items-center justify-content-center"
                    style={{ background: '#f8f9fa', borderRadius: 10, padding: '24px 16px', height: '100%' }}
                  >
                    <div
                      style={{
                        fontSize: 48,
                        fontWeight: 700,
                        color: RISK_LEVEL_COLOR[riskPosture.overall_risk_level] ?? '#6c757d'
                      }}
                    >
                      {riskPosture.overall_risk_score}
                    </div>
                    <div className="text-muted small mt-1">Overall Risk Score</div>
                    <span
                      className="badge mt-2 px-3 py-1"
                      style={{
                        background: RISK_LEVEL_COLOR[riskPosture.overall_risk_level] ?? '#6c757d',
                        color: '#fff',
                        borderRadius: 20,
                        fontSize: 12
                      }}
                    >
                      {riskPosture.overall_risk_level}
                    </span>
                  </div>
                </Col>

                <Col xs={12} md={4}>
                  <div className="fw-semibold small text-muted text-uppercase mb-2" style={{ fontSize: 11 }}>
                    Risk by Lens
                  </div>

                  {LENS_LABELS.map(lens => {
                    const riskScore = riskPosture.lens_risks[lens];
                    const pct = riskScore ?? 0;
                    const label = LENS_RISK_LABEL(riskScore);
                    const color = LENS_RISK_COLOR(riskScore);

                    return (
                      <div key={lens} className="mb-2">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span className="small">{lens}</span>
                          <div className="d-flex align-items-center gap-2">
                            <span className="small fw-semibold">{riskScore != null ? `${pct}%` : 'N/A'}</span>
                            <span
                              className="badge px-2 py-1"
                              style={{
                                background: color + '22',
                                color,
                                fontSize: 10,
                                fontWeight: 600,
                                borderRadius: 6
                              }}
                            >
                              {label}
                            </span>
                          </div>
                        </div>

                        <div style={{ height: 6, background: '#e9ecef', borderRadius: 3 }}>
                          <div
                            style={{
                              width: `${pct}%`,
                              background: color,
                              height: '100%',
                              borderRadius: 3,
                              transition: 'width 0.6s ease'
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </Col>

                <Col xs={12} md={4}>
                  <div className="fw-semibold small text-muted text-uppercase mb-2" style={{ fontSize: 11 }}>
                    Risk Insights
                  </div>

                  <div style={{ background: '#f8f9fa', borderRadius: 10, padding: '16px' }}>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="small text-muted">Highest Risk Lens</span>
                      <span className="small fw-semibold" style={{ color: '#dc3545' }}>
                        {riskPosture.weakest_lens}
                      </span>
                    </div>

                    <div className="d-flex justify-content-between mb-2">
                      <span className="small text-muted">Lowest Risk Lens</span>
                      <span className="small fw-semibold" style={{ color: '#28a745' }}>
                        {riskPosture.strongest_lens}
                      </span>
                    </div>

                    <div className="d-flex justify-content-between mb-2">
                      <span className="small text-muted">Risk Spread</span>
                      <span className="small fw-semibold">{riskPosture.risk_gap} pts</span>
                    </div>

                    <div className="d-flex justify-content-between mb-3">
                      <span className="small text-muted">Lenses at Risk</span>
                      <span className="small fw-semibold" style={{ color: '#dc3545' }}>
                        {Object.values(riskPosture.lens_risks).filter(v => v != null && v >= 40).length} /{' '}
                        {LENS_LABELS.length}
                      </span>
                    </div>

                    <div>
                      <div className="small text-muted fw-semibold mb-1 text-uppercase" style={{ fontSize: 10 }}>
                        Recommendation
                      </div>
                      <div className="small text-muted" style={{ lineHeight: 1.5 }}>
                        {riskPosture.recommendation}
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: 12 }}>
            <Card.Body className="p-4">
              <h6 className="fw-bold text-muted text-uppercase mb-3" style={{ fontSize: 12 }}>
                Lens Scores
              </h6>

              <Row className="g-3">
                {LENS_LABELS.map(lens => {
                  const score = metrics.lens_scores[lens];

                  const color =
                    score != null ? (score >= 75 ? '#28a745' : score >= 50 ? '#ffc107' : '#fd7e14') : '#6c757d';

                  return (
                    <Col xs={6} md={2} key={lens}>
                      <div
                        className="text-center"
                        style={{ background: '#f8f9fa', borderRadius: 10, padding: '16px 8px' }}
                      >
                        <div style={{ fontSize: 28, fontWeight: 700, color }}>{score ?? 'N/A'}</div>
                        <div className="text-muted small mt-1">{lens}</div>
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </Card.Body>
          </Card>

          <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: 12 }}>
            <Card.Body className="p-4">
              <h6 className="fw-bold text-muted text-uppercase mb-3" style={{ fontSize: 12 }}>
                Data Quality & Field Coverage
              </h6>

              <Row className="g-2">
                {Object.entries(metrics.dq_pct).map(([field, pct]) => (
                  <Col xs={12} md={6} lg={3} key={field}>
                    <div className="d-flex justify-content-between small mb-1">
                      <span className="text-muted">{field.replace(/_/g, ' ')}</span>
                      <span
                        className="fw-semibold"
                        style={{ color: pct >= 80 ? '#28a745' : pct >= 50 ? '#ffc107' : '#dc3545' }}
                      >
                        {pct}%
                      </span>
                    </div>

                    <div style={{ height: 6, background: '#e9ecef', borderRadius: 3 }}>
                      <div
                        style={{
                          width: `${pct}%`,
                          height: '100%',
                          borderRadius: 3,
                          background: pct >= 80 ? '#28a745' : pct >= 50 ? '#ffc107' : '#dc3545',
                          transition: 'width 0.6s ease'
                        }}
                      />
                    </div>
                  </Col>
                ))}
              </Row>
            </Card.Body>
          </Card>

          <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: 12 }}>
            <Card.Body className="p-4">
              <h6 className="fw-semibold mb-3">Plot 1 — Enterprise Scorecard Gauge</h6>
              {plot1Option ? (
                <ReactEChartsCore
                  echarts={echarts}
                  option={plot1Option}
                  notMerge
                  style={{ width: '100%', height: '220px' }}
                  opts={{ renderer: 'canvas' }}
                />
              ) : (
                <p className="text-muted small mb-0">No gauge data.</p>
              )}
            </Card.Body>
          </Card>

          <Row className="g-3 mb-3">
            <Col xs={12} md={6}>
              <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 12 }}>
                <Card.Body className="p-4">
                  <h6 className="fw-semibold mb-3">Plot 2 — 360° Radar (Current vs Target)</h6>
                  {plot2Option ? (
                    <ReactEChartsCore
                      echarts={echarts}
                      option={plot2Option}
                      notMerge
                      style={{ width: '100%', height: '340px' }}
                      opts={{ renderer: 'canvas' }}
                    />
                  ) : (
                    <p className="text-muted small mb-0">No radar data.</p>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} md={6}>
              <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 12 }}>
                <Card.Body className="p-4">
                  <h6 className="fw-semibold mb-3">Plot 3 — Ethical &amp; Security Risk Heatmap</h6>
                  {plot3Option ? (
                    <ReactEChartsCore
                      echarts={echarts}
                      option={plot3Option}
                      notMerge
                      style={{ width: '100%', height: '340px' }}
                      opts={{ renderer: 'canvas' }}
                    />
                  ) : (
                    <p className="text-muted small mb-0">No heatmap data.</p>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: 12 }}>
            <Card.Body className="p-4">
              <h6 className="fw-semibold mb-3">Plot 4 — Governance Stability Trend</h6>
              {plot4Option ? (
                <ReactEChartsCore
                  echarts={echarts}
                  option={plot4Option}
                  notMerge
                  style={{ width: '100%', height: '300px' }}
                  opts={{ renderer: 'canvas' }}
                />
              ) : (
                <p className="text-muted small mb-0">No trend data.</p>
              )}
            </Card.Body>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AIAssurancePage;
